// Configuration and shared types for the multiplayer fork. See
// multiplayer/README.md for setup instructions.

export const SUPABASE_URL = "https://olkkshnxaktcgcmhltjl.supabase.co";
export const SUPABASE_PUBLISHABLE_KEY =
	"sb_publishable_SKnOn9B1u03H9UZe9om4YQ_P-6f326g";

// Bump whenever the Supabase schema, snapshot format, or delta format changes
// incompatibly. Clients whose version differs from the league's cannot join
// it, which protects leagues from being corrupted by out-of-date clients.
// v2: Phase 2 - trade events, asset ledger, offers
// v3: Phase 3 - checkpoints, ready-up, move budgets
// v4: Phase 4 - synced simulation (sim events, sim_open window)
// v5: Phase 5 - blind-bid free agency (fa checkpoint, fa_round events)
// v6: Phase 5b - re-sign checkpoint (resign events, candidates)
// v7: Phase 6 - shared playoffs (playoff slice pauses, alive ready-gate)
// v8: Phase 7a - commissioner rules (auto-firing + reassignment)
// v9: Phase 6b - live multiplayer draft (humans pick on their own screens)
// v10: playoffs checkpoint - one ready-up before the playoffs, then the runner
//      sims freely to the draft (replaces pause-per-human-game slices); free
//      agency is a single blind-bid round
// v11: synced lineups - roster order / playing time / play-through-injury lock
//      in at ready-up ('lineup' events), and the playoffs sim runs without
//      spectator so those settings are actually used
// v12: live fantasy draft at league start (replaces the silent auto-scramble);
//      auto_draft member flag = "sim the rest of my picks"
// v13: era-correct rules - league settings are seeded from the starting
//      season's real game attributes instead of modern defaults, so a 2011
//      league runs with the 2011 salary cap ($58M not $150M), no play-in, the
//      nba1994 lottery, etc., and rule changes still arrive over the years via
//      scheduled events. Also: the AI no longer signs free agents onto
//      human-controlled teams during sims. (Fresh league required: older
//      leagues have the wrong rules permanently baked into their snapshot.)
// v14: free agents can be signed directly (first-come-first-serve) any time
//      outside the blind-bid auction, like normal BBGM - new 'signing' event +
//      sign_free_agent RPC. Also a min-roster-size ready-up gate at the
//      offseason checkpoint (no schema change for that part).
// v15: more offseason trade windows - a new "Draft lottery" checkpoint (pauses
//      right after the lottery sets pick order, so teams can trade for a known
//      lottery pick), and trading is now allowed during the Re-sign and Free
//      agency checkpoints (default 2 each). The Free agency checkpoint is the
//      last stop before players develop, so it doubles as the pre-progression
//      preseason trade window. The Offseason checkpoint is relabeled
//      "Preseason". Schema change (mp_budget defaults + complete_sim cycle).
// v16: expansion drafts + releasing players. When a real-league expansion
//      (e.g. Dallas 1980) fires mid-sim, the runner pauses and publishes so
//      EVERY human protects their own players ('protect' events + a ready-up),
//      then the runner resolves the expansion draft and continues. Releasing
//      players now works like vanilla (dead money owed until the contract
//      expires) via 'release' events. Schema change (expansion_active,
//      publish_expansion_pause, submit_protect, release_player, complete_sim).
// v16.3 (no bump - schema-only, existing leagues get it): free agency and
//      re-signing behave like vanilla BBGM. Phantom bids fixed (every FA
//      window reopens at round 1, so leftover rows from a previous season
//      resurfaced as bids nobody placed - resolve_fa_round and complete_sim
//      now clear fa_bids). Re-signing respects mood: an unwilling player shows
//      "Refuses to re-sign!" and resign_player rejects him, and the asking
//      price is the mood-adjusted per-team demand rather than the raw number.
//      Contract demands scale with the number of years offered on both the
//      re-sign and free agent pages, matching generateContractOptions exactly.
export const MP_PROTOCOL_VERSION = 16;

export const DEFAULT_FIRING = {
	autoFiring: true,
	noProfitYears: 4, // fire after this many straight unprofitable seasons
	noPlayoffsYears: 5,
	noConfFinalsYears: 10,
	failUpwardPct: 0.12, // chance a fired GM "fails upward" to a top-4 team
};

// The season clock cycles through these stops; between them the sim-runner
// advances the games. Budgets = trades allowed per team per stop. At the
// resign stop each human also re-signs their own expiring players, and at the
// fa stop blind-bid auction rounds run - both now allow trading too. Only the
// playoffs stop forbids trading (rosters are locked for the bracket).
export const CHECKPOINT_CYCLE = [
	"offseason",
	"game25",
	"deadline",
	"playoffs",
	"lottery",
	"draft",
	"resign",
	"fa",
] as const;
export type MpCheckpoint = (typeof CHECKPOINT_CYCLE)[number];

export const CHECKPOINT_LABELS: Record<MpCheckpoint, string> = {
	offseason: "Preseason",
	game25: "Game 25",
	deadline: "Trade deadline",
	playoffs: "Playoffs",
	lottery: "Draft lottery",
	draft: "Draft",
	resign: "Re-sign players",
	fa: "Free agency",
};

export const DEFAULT_BUDGETS: Record<MpCheckpoint, number> = {
	offseason: 4,
	game25: 2,
	deadline: 3,
	playoffs: 0,
	lottery: 2,
	draft: 2,
	resign: 2,
	fa: 2,
};

// Stops whose sim segment plays games, so an oversized roster is blocked before
// the league can be waved forward: offseason -> game 25 -> deadline -> playoffs
// -> the bracket itself. The playoffs stop is included even though trading is
// closed there (budget 0), because direct free-agent signing is not -
// sign_free_agent only refuses during the auction, the draft, and an active sim
// - so a roster that was legal at the deadline can be over the max by the time
// the bracket starts.
export const ROSTER_LIMIT_CHECKPOINTS = new Set<MpCheckpoint>([
	"offseason",
	"game25",
	"deadline",
	"playoffs",
]);

// Being UNDER the minimum only blocks ready-up at these two, because everywhere
// else the engine quietly fixes it. The runner sims game25/deadline segments
// with spectator mode ON, which makes checkRosterSizes treat human teams as AI
// and top a short roster up with minimum-salary free agents - so blocking there
// would be pointless. The two exceptions:
//   - offseason: the top-up would work, but we deliberately refuse it. Opening
//     day rosters should be filled by their humans, not by AI scrubs.
//   - playoffs: beginSegment turns spectator OFF for the bracket (humans manage
//     their own lineups through it), so there is NO top-up. A short human roster
//     makes checkRosterSizes("user") return an error that aborts the runner's
//     sim outright, stranding a league where everyone has already readied up.
export const MIN_ROSTER_CHECKPOINTS = new Set<MpCheckpoint>([
	"offseason",
	"playoffs",
]);

// Releasing players (dead money) needs the release_player RPC + 'release' event
// (protocol v16). v16 leagues have the migration, so it's on.
export const MP_RELEASE_ENABLED = true;

// How many seasons of future draft picks exist (and are therefore tradable).
// Vanilla BBGM's default is 4; multiplayer leagues use a longer horizon so
// teams can deal picks further out. Applied to new leagues at creation and
// lazily raised in existing leagues on the runner's next sim (see beginSegment)
// - a gameAttribute change, so no schema/protocol bump and no fresh league.
export const MP_FUTURE_DRAFT_PICK_SEASONS = 7;

export type MpLeagueStatus = "lobby" | "starting" | "active";

export type MpLeagueSettings = {
	bandFactor?: number;
	// "real" = real players (basketball only), "random" = fictional players
	rosters?: "real" | "random";
	// Only for rosters === "real": which season the league starts in
	startingSeason?: number;
	// Trades allowed per human team per checkpoint (defaults above)
	budgets?: Partial<Record<MpCheckpoint, number>>;
	// 0-1: scales down the team-performance mood component in free agency so
	// championship teams can't hoard every free agent (0 = vanilla BBGM)
	faWinnerDampen?: number;
	// League difficulty (see constants.DIFFICULTY): Easy -0.25, Normal 0,
	// Hard 0.25, Insane 1
	difficulty?: number;
	// Commissioner rules (Phase 7): auto-firing thresholds. See DEFAULT_FIRING.
	autoFiring?: boolean;
	noProfitYears?: number;
	noPlayoffsYears?: number;
	noConfFinalsYears?: number;
	failUpwardPct?: number;
};

export type MpLeague = {
	id: string;
	name: string;
	invite_code: string;
	app_version: number;
	status: MpLeagueStatus;
	commissioner: string;
	settings: MpLeagueSettings;
	snapshot_path: string | null;
	created_at: string;
	checkpoint: MpCheckpoint;
	checkpoint_seq: number;
	sim_open: boolean;
	sim_runner: string | null;
	snapshot_seq: number;
	fa_open: boolean;
	fa_round: number;
	playoffs_active: boolean;
	playoff_round: number;
	playoff_alive: number[];
	draft_active: boolean;
	draft_on_clock: number | null;
	draft_pending_pid: number | null;
	// v16: mid-segment expansion draft pause (everyone protects players)
	expansion_active?: boolean;
};

export type MpMember = {
	league_id: string;
	user_id: string;
	display_name: string;
	tid: number | null;
	out_of_band: boolean;
	ready: boolean;
	role: "commissioner" | "member";
	joined_at: string;
	tenure_start_season?: number | null;
	auto_draft?: boolean;
};

export type MpPendingFiring = {
	league_id: string;
	user_id: string;
	tid: number;
	new_tid: number;
	reason: string;
	fail_upward: boolean;
	season: number;
};

export type MpTeam = {
	league_id: string;
	tid: number;
	region: string;
	name: string;
	abbrev: string;
	pop: number;
};

export type MpSession = {
	userId: string;
	email: string;
};

// A team is "in band" when its population is within bandFactor of the median
// population of teams claimed by other members. No claims yet = everything in
// band.
export const isInBand = (
	pop: number,
	medianPop: number | undefined,
	bandFactor: number,
) => {
	if (medianPop === undefined) {
		return true;
	}
	return pop >= medianPop / bandFactor && pop <= medianPop * bandFactor;
};

export const DEFAULT_BAND_FACTOR = 2;

export const medianClaimedPop = (
	members: MpMember[],
	teams: MpTeam[],
	excludeUserId?: string,
) => {
	const pops = members
		.filter((m) => m.tid !== null && m.user_id !== excludeUserId)
		.map((m) => teams.find((t) => t.tid === m.tid)?.pop)
		.filter((pop): pop is number => pop !== undefined)
		.sort((a, b) => a - b);

	if (pops.length === 0) {
		return undefined;
	}

	const mid = Math.floor(pops.length / 2);
	return pops.length % 2 === 1 ? pops[mid]! : (pops[mid - 1]! + pops[mid]!) / 2;
};
